/**
 * Global axios error interceptor — captures network/server errors,
 * sends them to /api/logs/client-error with full device context,
 * and exposes a Ref-ID the user can quote when raising support.
 *
 * Usage: imported once in src/index.js. Toasts are dispatched via window
 * event "is:error-with-ref" so any toast lib can listen.
 */
import axios from "axios";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

let lastReportSent = 0; // throttle to one report per 2s per session

function deviceContext() {
  try {
    return {
      route: window.location.pathname + window.location.search,
      screen: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      platform: navigator.platform || "",
    };
  } catch {
    return {};
  }
}

async function reportClientError(payload) {
  const now = Date.now();
  if (now - lastReportSent < 2000) return null;
  lastReportSent = now;
  try {
    const res = await axios.post(`${API_BASE}/logs/client-error`, payload, {
      withCredentials: true,
      timeout: 8000,
    });
    return res.data?.ref_id || null;
  } catch {
    return null;
  }
}

export function installErrorInterceptor() {
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      // Skip if this IS the client-error endpoint itself (avoid loops)
      const reqUrl = error.config?.url || "";
      if (reqUrl.includes("/logs/client-error")) {
        return Promise.reject(error);
      }

      const isNetwork = !error.response;
      const status = error.response?.status;
      const isServerError = status >= 500;
      const isAuthError = status === 401 || status === 403;
      const isHttpReply = !!error.response;

      // Token expiry handling — let the auth context handle 401 silently
      const tokenExpired = isAuthError;

      // Only log: network errors, server (5xx), or unknown bodies. Skip 4xx user errors.
      if (isNetwork || isServerError) {
        const ref = await reportClientError({
          level: isNetwork ? "warning" : "error",
          message: error.message || "Unknown error",
          endpoint: reqUrl,
          status_code: status || 0,
          stack: (error.stack || "").slice(0, 4000),
          token_expired: tokenExpired,
          ...deviceContext(),
        });

        // If we got a ref back, surface it to the user via toast.
        if (ref) {
          if (isNetwork) {
            toast.error(`Network issue. Please retry. Ref ID: ${ref}`, { duration: 6000 });
          } else if (isServerError) {
            toast.error(`Something went wrong on our side. Ref ID: ${ref}`, { duration: 6000 });
          }
          // Attach Ref-ID to the error so callers can show it
          error.refId = ref;
        }

        // Dispatch a window event for any non-toast listeners (admin debug bar, etc.)
        try {
          window.dispatchEvent(new CustomEvent("is:error-with-ref", {
            detail: { refId: ref, status, endpoint: reqUrl, message: error.message },
          }));
        } catch { /* ignore */ }
      } else if (isHttpReply && error.response?.headers?.["x-request-ref"]) {
        // Surface backend-supplied Ref-ID even for 4xx so user can quote it on support
        error.refId = error.response.headers["x-request-ref"];
      }

      return Promise.reject(error);
    }
  );
}
